import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Alert,
  Skeleton,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { withTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useVehicleProfitabilityReport } from '../hooks/useReportsApi';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function VehicleProfitabilitySection({ t, dateParams }) {
  const { data, loading, error } = useVehicleProfitabilityReport(dateParams);
  const [sortField, setSortField] = useState('total_revenue');
  const [sortDirection, setSortDirection] = useState('desc');

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedVehicles = [...(data?.vehicles || [])].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Top 10 chart data
  const chartData = (data?.top_vehicles || []).map((v) => ({
    name: v.registration_number || t('reports.noData'),
    labor: v.labor_revenue,
    parts: v.parts_revenue,
  }));

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="profitability/vehicles" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={DirectionsCarIcon}
            label={t('reports.profitability.vehiclesServiced')}
            value={data?.vehicle_count}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.profitability.avgRevenuePerVehicle')}
            value={data?.avg_revenue_per_vehicle}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={TrendingUpIcon}
            label={t('reports.profitability.avgMarginPerVehicle')}
            value={data?.avg_margin_per_vehicle}
            loading={loading}
            format="currency"
            iconColor="#10b981"
            iconBgColor="rgba(16, 185, 129, 0.1)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={ReceiptIcon}
            label={t('reports.profitability.avgWosPerVehicle')}
            value={data?.avg_wos_per_vehicle}
            loading={loading}
            format="decimal"
          />
        </Grid>
      </Grid>

      {/* Top 10 Chart */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.profitability.topVehicles')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={350} />
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="labor" name={t('reports.profitability.labor')} fill="#3182ce" stackId="a" />
              <Bar dataKey="parts" name={t('reports.profitability.parts')} fill="#10b981" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">{t('reports.noData')}</Typography>
          </Box>
        )}
      </Paper>

      {/* Vehicles Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.profitability.vehiclesDetail')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel active={sortField === 'registration_number'} direction={sortField === 'registration_number' ? sortDirection : 'asc'} onClick={() => handleSort('registration_number')}>
                      {t('reports.profitability.registrationNumber')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('reports.profitability.makeModel')}</TableCell>
                  <TableCell>{t('reports.profitability.customer')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.workOrderCount')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.labor')}</TableCell>
                  <TableCell align="right">{t('reports.profitability.parts')}</TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'parts_margin'} direction={sortField === 'parts_margin' ? sortDirection : 'asc'} onClick={() => handleSort('parts_margin')}>
                      {t('reports.profitability.margin')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">{t('reports.profitability.marginPercent')}</TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'total_revenue'} direction={sortField === 'total_revenue' ? sortDirection : 'asc'} onClick={() => handleSort('total_revenue')}>
                      {t('reports.profitability.total')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVehicles.map((v, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 500 }}>{v.registration_number || '-'}</TableCell>
                    <TableCell>{v.make_model || '-'}</TableCell>
                    <TableCell>{v.customer_name}</TableCell>
                    <TableCell align="right">{v.work_order_count}</TableCell>
                    <TableCell align="right">{formatCurrency(v.labor_revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(v.parts_revenue)}</TableCell>
                    <TableCell align="right">
                      <Typography
                        component="span"
                        sx={{ color: v.parts_margin >= 0 ? 'success.main' : 'error.main', fontWeight: 500 }}
                      >
                        {formatCurrency(v.parts_margin)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {v.parts_margin_percent !== null ? `${v.parts_margin_percent.toFixed(1)} %` : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(v.total_revenue)}</TableCell>
                  </TableRow>
                ))}
                {sortedVehicles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
                {sortedVehicles.length > 0 && (() => {
                  const totals = sortedVehicles.reduce(
                    (acc, v) => ({
                      wos: acc.wos + v.work_order_count,
                      labor: acc.labor + v.labor_revenue,
                      parts: acc.parts + v.parts_revenue,
                      margin: acc.margin + v.parts_margin,
                      total: acc.total + v.total_revenue,
                    }),
                    { wos: 0, labor: 0, parts: 0, margin: 0, total: 0 }
                  );
                  return (
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>{t('reports.profitability.total')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.wos}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.labor)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.parts)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.margin)}</TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(totals.total)}</TableCell>
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(VehicleProfitabilitySection);
